<script setup lang="ts">
import { storeToRefs } from 'pinia';
import * as Sentry from '@sentry/vue';
import RouletteWheel from './RouletteWheel.vue';
import RoulettePrizeHistory from './RoulettePrizeHistory.vue';
import { useRouletteStore } from '@/stores/roulette';
import { useRouletteSounds } from '~/composables/apostatudo/use-roulette-sounds';
import { getAssetsUrlForCDN } from '@/utils/template';
import { prizeDisplayImage } from '~/adapters/api-roulette/roulette.mappers';
import { vipTierStyle } from '@/utils/vip-tiers';
import {
  RARITY_LABELS,
  rarityStyle,
  rarityTier,
} from '~/utils/apostatudo/roulette-rarity';
import type { RoulettePopup, RoulettePrize } from '@/types/roulette';

const TAGLINE_FALLBACK =
  'Gire a roleta diária Apostatudo para conquistar os melhores prêmios!';

const store = useRouletteStore();
const {
  isOpen,
  status,
  detail,
  prizes,
  availableSpins,
  nextSpinAvailableAt,
  slug,
  canSpin,
  errorMessage,
  lastSpinResult,
} = storeToRefs(store);

// Estados que NÃO abrem a roletinha e sim o modal de aviso (BaseNoticeSheet):
// sem roda, sem topbar, sem tagline — arte de cabeçalho e o motivo. O sheet
// cheio não servia: a altura travada dele existe pra composição da roda não
// esticar em tela alta, e sem roda ela só produzia um vazio embaixo de uma linha
// de texto.
//
// Só estes dois, e não 'error'/'loading': os dois são decididos no `open()`,
// ANTES do sheet subir, então já é o aviso que sobe. 'loading' e 'error' chegam
// depois de uma folha cheia no ar, e trocar a folha no meio do caminho seria um
// salto seco na cara do jogador (o reset global mata `transition`).
const isNotice = computed(
  () => status.value === 'unavailable' || status.value === 'ineligible',
);

// A frase do aviso é o MOTIVO, e ele muda com o estado. A da elegibilidade fala
// de "usuários ativos", que é mais largo do que a regra real (`is_eligible` = já
// fez pelo menos uma aposta real): escolha de produto, a frase não instrui a
// apostar. Quem lista o que falta é a seção de requisitos, só nesse estado.
const noticeMessage = computed(() =>
  status.value === 'ineligible'
    ? 'Função disponível apenas para usuários ativos na plataforma.'
    : 'Nenhuma roleta disponível para você no momento.',
);

// Cabeçalho do aviso: a RODA DO TIER do jogador, a mesma arte que o card dele no
// Club Fidelidade usa (`vipTierStyle`) — quem é VIP 4 vê a roda do VIP 4 aqui
// também. Sem tier desenhado (jogador comum, e o SIGNATURE, que ainda não tem
// arte) cai na composição do Figma, com o ribbon "ROLETA GRATUITA".
//
// As duas artes têm proporções diferentes; quem resolve isso é a janela do
// BaseNoticeSheet, que recorta por `object-fit: cover`.
const { vipTier } = useVipStatus();

const noticeArt = computed(
  () => vipTierStyle(vipTier.value)?.wheel ?? asset('aviso-header.webp'),
);

const wheel = ref<InstanceType<typeof RouletteWheel> | null>(null);

// `visible` mantém o overlay montado até a animação de saída terminar;
// `sheetOpen` dispara o slide-up/slide-down da tela.
const visible = ref(false);
const sheetOpen = ref(false);

// Dropdown do menu (⋮), ancorado no botão do topo.
const menuOpen = ref(false);

// Sub-tela aberta sobre a roda (tela cheia, com "Voltar"). null = roda.
type RoulettePanel = 'history' | 'terms' | 'prizes';

const activePanel = ref<RoulettePanel | null>(null);

// true quando a sub-tela terminou o slide-up e cobre o sheet. Enquanto isso,
// roda/glow saem do paint (display:none): qualquer pixel claro atrás do painel
// vaza no anti-aliasing das quinas arredondadas (o "defeito das bordas
// inferiores"). A roda segue visível durante as animações de entrada/saída.
const panelSettled = ref(false);

const panelTitle = computed(() => {
  switch (activePanel.value) {
    case 'history':
      return 'Histórico';

    case 'terms':
      return 'Termos e Condições';

    case 'prizes':
      return 'Prêmios Disponíveis';

    default:
      return '';
  }
});

// Abre uma sub-tela a partir do dropdown (e fecha o dropdown).
function openPanel(panel: RoulettePanel) {
  activePanel.value = panel;
  menuOpen.value = false;
}

// "Voltar": fecha a sub-tela e volta pra roda.
function onPanelBack() {
  activePanel.value = null;
}

// Sons da roleta (Web Audio, iOS-safe).
const sounds = useRouletteSounds();

// Efeito "clac": a cada divisória que passa pelo ponteiro (evento tick da roda),
// o pino dá um tranco rápido e volta + som. Mantém o translateX(-50%) do CSS.
const marker = ref<HTMLElement | null>(null);
let tickAnim: Animation | null = null;

function onPinTick() {
  sounds.playTick();

  const el = marker.value;

  if (!el) return;

  tickAnim?.cancel();
  tickAnim = el.animate(
    [
      { transform: 'translateX(-50%) rotate(0deg)' },
      { transform: 'translateX(-50%) rotate(-8deg)' },
      { transform: 'translateX(-50%) rotate(0deg)' },
    ],
    { duration: 120, easing: 'ease-out' },
  );
}

const popupOpen = ref(false);
const activePopup = ref<RoulettePopup | null>(null);

// Modal de erro do giro (rede/lentidão, 429, delivery_failed, api): substitui a
// pílula inline. A mensagem já vem resolvida por kind na store (errorMessage).
const errorOpen = ref(false);

// Prêmio sorteado (por id) — fonte da imagem, do nome e da raridade (fita +
// glow) do modal de vitória. Null na derrota (ou quando não resolve por id).
const wonPrize = computed(
  () =>
    prizes.value.find((p) => p.id === lastSpinResult.value?.prize?.id) ?? null,
);

// Imagem central do popup de vitória: cadeia única do prizeDisplayImage sobre o
// prêmio sorteado. Na derrota fica null (o popup usa a imagem do lose_popup).
const activePrizeImage = computed(() =>
  lastSpinResult.value?.result === 'win'
    ? prizeDisplayImage(wonPrize.value) || null
    : null,
);

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/roleta/${file}`);

// Vazia enquanto o detalhe não chega (não trocar de frase durante a abertura
// do modal); depois usa a descrição do core, com fallback do Figma.
const tagline = computed(() =>
  detail.value ? detail.value.description || TAGLINE_FALLBACK : '',
);

// "Prêmios Disponíveis" lista só as fatias de PRÊMIO — a derrota fica de fora
// (não é prêmio). display_probability vem calculada do core; sem o campo, a
// porcentagem não aparece. Ordem: prêmios mais raros primeiro.
const panelPrizes = computed(() =>
  prizes.value
    .filter((prize) => !prize.isLoss)
    .sort(
      (a, b) =>
        (a.displayProbability ?? Number.POSITIVE_INFINITY) -
        (b.displayProbability ?? Number.POSITIVE_INFINITY),
    ),
);

function probabilityLabel(prize: RoulettePrize): string {
  if (prize.displayProbability === null) return '';

  return `${prize.displayProbability.toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  })}%`;
}

// Cor do glow: ring_custom.glow_color do core quando enviado; senão cai no
// slice_bg do prêmio.
const glowColor = computed(
  () =>
    detail.value?.ringCustom?.glowColor ||
    prizes.value[0]?.colors?.sliceBg ||
    '#4f46e5',
);

// Glow grande atrás da tagline e da roda.
const glowStyle = computed(() => ({
  background: `radial-gradient(circle, ${glowColor.value} 0%, transparent 62%)`,
}));

// Cor de destaque do sheet: as partículas do fundo. É FIXA e não acompanha o glow, que vem do core (`ring_custom.glow_color`):
// amarrar as duas camadas na mesma cor apagava a distinção entre elas e deixava o
// sheet monocromático. O azul aqui é o contraponto do dourado do check-in.
const ACCENT_COLOR = '#5AA9FF';

// Rótulo do botão central nos estados PARADOS — 'spinning' não passa por aqui:
// o template troca o texto pelo loader dos 3 pontinhos enquanto a roda gira.
const spinCenterLabel = computed(() => {
  if (availableSpins.value <= 0) return 'SEM\nGIROS';

  // Texto do estado "pode girar" vem do core (ring_custom.button_text).
  return detail.value?.ringCustom?.buttonText || 'GIRAR';
});

// Cor do botão central vem do core (ring_custom.button_color): gradiente + borda
// derivados dela. Sem valor, cai no rosa do CSS.
const spinStyle = computed(() => {
  const c = detail.value?.ringCustom?.buttonColor;

  if (!c) return {};

  return {
    background: `radial-gradient(circle at 50% 35%, color-mix(in srgb, #fff 22%, ${c}) 0%, ${c} 70%)`,
    borderColor: `color-mix(in srgb, #fff 38%, ${c})`,
  };
});

// Contagem regressiva até o próximo giro, quando o usuário está sem giros.
const now = ref(Date.now());
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const nextSpinMs = computed(() => {
  const ms = nextSpinAvailableAt.value
    ? Date.parse(nextSpinAvailableAt.value)
    : NaN;

  return Number.isNaN(ms) ? null : ms;
});

const remainingMs = computed(() =>
  nextSpinMs.value === null ? 0 : Math.max(0, nextSpinMs.value - now.value),
);

// Mostra o countdown no botão só quando faz sentido: pronto, sem giros e com um
// horário futuro. Sem horário válido cai no rótulo "SEM GIROS".
const showCountdown = computed(
  () =>
    status.value === 'ready' &&
    availableSpins.value <= 0 &&
    remainingMs.value > 0,
);

const countdownLabel = computed(() => {
  const total = Math.floor(remainingMs.value / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(Math.floor(total / 3600))}:${pad(
    Math.floor((total % 3600) / 60),
  )}:${pad(total % 60)}`;
});

function startCountdown() {
  stopCountdown();
  now.value = Date.now();
  countdownTimer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
}

function stopCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

// O tick de 1s só roda enquanto há countdown a mostrar (sem giros + horário
// futuro conhecido); fora disso o interval fica parado.
const countdownNeeded = computed(
  () =>
    status.value === 'ready' &&
    availableSpins.value <= 0 &&
    nextSpinMs.value !== null,
);

watch(countdownNeeded, (needed) =>
  needed ? startCountdown() : stopCountdown(),
);

// Quando a contagem zera, recarrega o detalhe pra reabilitar o giro sem o
// usuário precisar reabrir o modal.
watch(remainingMs, (val, prev) => {
  if (prev > 0 && val === 0 && slug.value) {
    store.loadDetail(slug.value);
  }
});

// Refresh silencioso por inatividade e ao retomar a aba: recarrega só o detalhe
// (availableSpins, próximo giro) pra o usuário não ficar vendo uma versão velha
// do modal depois de deixá-lo aberto e parado. Nunca dispara durante o giro nem
// com popup/erro na tela — a guarda garante que não atrapalha nada.
const IDLE_REFRESH_MS = 3 * 60 * 1000;
let idleTimer: ReturnType<typeof setTimeout> | null = null;

// Só é seguro refrescar com a roda parada (ready), sem popup de resultado nem
// modal de erro abertos, e com uma roleta resolvida.
function canRefreshNow() {
  return (
    isOpen.value &&
    status.value === 'ready' &&
    !popupOpen.value &&
    !errorOpen.value &&
    !!slug.value
  );
}

function maybeRefresh() {
  if (!canRefreshNow()) return;
  store.loadDetail(slug.value, { silent: true });
}

function clearIdleTimer() {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

// (Re)arma a contagem de ociosidade. Ao disparar, tenta o refresh e re-arma pro
// próximo ciclo enquanto o modal seguir aberto. Se cair no meio de um giro, a
// guarda pula o refresh (o próprio giro já atualiza os giros restantes).
function scheduleIdleRefresh() {
  clearIdleTimer();
  idleTimer = setTimeout(() => {
    maybeRefresh();
    scheduleIdleRefresh();
  }, IDLE_REFRESH_MS);
}

// Qualquer interação no modal zera a contagem de ociosidade.
function onUserActivity() {
  if (isOpen.value) scheduleIdleRefresh();
}

// Voltar pra aba depois de sair é o momento mais provável de dado velho:
// refresca na hora e reinicia o timer de ociosidade.
function onVisibilityChange() {
  if (document.visibilityState !== 'visible') return;
  maybeRefresh();
  scheduleIdleRefresh();
}

// Abertura/fechamento controlados pela store (modal global, sem navegação).
watch(
  isOpen,
  async (opened) => {
    if (opened) {
      visible.value = true;
      activePanel.value = null;
      menuOpen.value = false;
      document.body.style.overflow = 'hidden';
      await nextTick();
      sheetOpen.value = true;
      // init() é disparado só no @after-enter, pra não rodar fetch + render
      // do SVG da roda no meio da animação de slide-up (causa travamento).
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  document.body.style.overflow = '';
  stopCountdown();
  clearIdleTimer();
  document.removeEventListener('visibilitychange', onVisibilityChange);
});

async function handleSpin() {
  if (!canSpin.value) return;

  // SYNC no gesto (obrigatório iOS): desbloqueia o áudio antes de qualquer await.
  sounds.unlock();
  await sounds.ensureDecoded();

  const result = await store.spin();

  // Falha no giro: a store já classificou e populou errorMessage — abre o modal
  // de erro (o status volta pra 'ready', então dá pra tentar de novo).
  if (!result) {
    errorOpen.value = true;

    return;
  }

  // Resolve a fatia onde a roda pousa — o resultado já veio fechado do servidor.
  // Destino canônico = result.slice.position. Fallbacks só como defesa:
  // - Vitória: sem slice, deriva por position/id do prêmio. NUNCA pousa em fatia
  //   aleatória (contradiria o prêmio). Sem resolver, pula o pouso e vai direto
  //   pro popup (fonte de verdade do prêmio) e reporta ao Sentry.
  // - Derrota: sem slice, pousa numa fatia de DERROTA (is_loss) aleatória — nunca
  //   numa de prêmio. Roleta sem fatia de derrota cai em qualquer uma.
  let landing: number | null = result.slice?.position ?? null;

  if (landing === null) {
    if (result.result === 'win') {
      landing =
        result.prize?.position ??
        prizes.value.find((prize) => prize.id === result.prize?.id)?.position ??
        null;

      if (landing === null) {
        Sentry.captureException(
          new Error('Roleta: vitória sem fatia resolvível (slice/position/id)'),
          {
            tags: { source: 'roulette.landing' },
            extra: { prizeId: result.prize?.id, slug: slug.value },
          },
        );
      }
    } else {
      const lossSlices = prizes.value.filter((prize) => prize.isLoss);
      const visualPool = lossSlices.length ? lossSlices : prizes.value;

      landing =
        visualPool[Math.floor(Math.random() * visualPool.length)]?.position ??
        null;
    }
  }

  if (landing !== null) await wheel.value?.spinTo(landing);

  // Fim do giro: "pousa" na fatia; comemora se ganhou.
  sounds.playLand();

  if (result.result === 'win') sounds.playWin();

  store.settleSpin();

  activePopup.value =
    result.result === 'win'
      ? (result.prize?.popup ?? null)
      : (result.losePopup ?? detail.value?.losePopup ?? null);

  popupOpen.value = !!activePopup.value;
}

function close() {
  // Enquanto a roda gira o usuário não pode sair (nem pelo X, nem pelo clique
  // no backdrop): o giro já foi consumido no servidor e sair no meio deixaria
  // o resultado sem popup. Espera o giro assentar (settleSpin → 'ready').
  if (status.value === 'spinning') return;
  store.close();
}

// "Tentar novamente" do modal de erro: o giro falho não foi consumido/foi
// estornado, então canSpin segue verdadeiro e dá pra girar na hora.
function onErrorRetry() {
  handleSpin();
}

// Fechar/dispensar o modal de erro: re-resolve a roleta do zero (lista → data[0]
// → detalhe). Erros como "roleta não disponível" (horário vencido) travariam num
// loop de giro na roleta antiga; o init pega a roleta atual (ou cai em
// unavailable) e atualiza canSpin, quebrando o loop.
function onErrorClose() {
  store.init();
}

function onOpened() {
  // Conteúdo pesado (fetch + render da roda) só depois do slide-up terminar.
  store.init();
  sounds.preload(); // baixa os sons (sem gesto); o unlock vem no clique do GIRAR
  document.addEventListener('visibilitychange', onVisibilityChange);
  scheduleIdleRefresh();
}

function onClosed() {
  visible.value = false;
  activePanel.value = null;
  // Reset direto: o before-leave do painel não roda quando o sheet inteiro
  // desmonta com a sub-tela aberta.
  panelSettled.value = false;
  menuOpen.value = false;
  // Popup local acompanha o reset da store (senão reabre com resultado velho).
  popupOpen.value = false;
  activePopup.value = null;
  errorOpen.value = false;
  document.body.style.overflow = '';
  stopCountdown();
  clearIdleTimer();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  sounds.dispose(); // iOS suspende o contexto entre sessões
  store.reset();
}
</script>

<template>
  <Teleport to="body">
    <!-- dragstart.prevent por delegação: nenhuma imagem/texto do modal pode
         ser arrastado (o CSS user-drag não cobre Firefox). -->
    <div
      v-if="visible"
      class="rlt-overlay"
      @dragstart.prevent
    >
      <div
        class="rlt-backdrop"
        :class="{ 'rlt-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        name="slide-up"
        @after-enter="onOpened"
        @after-leave="onClosed"
      >
        <!-- Sem roleta pra jogar, sobe o modal de aviso no lugar da folha: a
             roda, a topbar e a tagline não têm o que fazer nesses estados. -->
        <BaseNoticeSheet
          v-if="sheetOpen && isNotice"
          :accent="ACCENT_COLOR"
          :art="noticeArt"
          :message="noticeMessage"
          :requirements="status === 'ineligible'"
          @close="close"
        />

        <div
          v-else-if="sheetOpen"
          class="rlt-screen"
          @pointerdown="onUserActivity"
        >
          <!-- Glow de fundo na cor da borda (do core): grande, cobre atrás da
               tagline e da roda. -->
          <div
            v-show="!panelSettled"
            class="rlt-glow"
            :style="glowStyle"
          />

          <!-- Partículas subindo no fundo, na frente do glow e atrás de tudo o
               mais. Somem junto com o glow quando uma sub-tela cobre o sheet.
               A cor é fixa e NÃO acompanha o glow (que vem do core): as duas
               camadas tinham a mesma cor e a roleta ficava monocromática — o azul
               aqui é o contraponto do dourado do check-in. -->
          <BaseParticlesField
            v-show="!panelSettled"
            class="rlt-particles"
            :color="ACCENT_COLOR"
          />

          <header class="rlt-topbar">
            <div class="rlt-topbar__left">
              <div class="rlt-menuwrap">
                <button
                  aria-label="Menu"
                  class="rlt-icon"
                  type="button"
                  @click="menuOpen = !menuOpen"
                >
                  <img
                    alt=""
                    :src="asset('menu-dots.svg')"
                  />
                </button>

                <!-- Dropdown do menu (⋮): abre as 3 sub-telas. -->
                <Transition name="rlt-dd">
                  <ul
                    v-if="menuOpen"
                    class="rlt-dropdown"
                  >
                    <li>
                      <button
                        class="rlt-dropdown__item"
                        type="button"
                        @click="openPanel('history')"
                      >
                        Histórico
                      </button>
                    </li>
                    <li>
                      <button
                        class="rlt-dropdown__item"
                        type="button"
                        @click="openPanel('terms')"
                      >
                        Termos e Condições
                      </button>
                    </li>
                    <li>
                      <button
                        class="rlt-dropdown__item"
                        type="button"
                        @click="openPanel('prizes')"
                      >
                        Prêmios Disponíveis
                      </button>
                    </li>
                  </ul>
                </Transition>
              </div>

              <!-- Atalho do histórico (presente), lado a lado com o ⋮ — Figma
                   node 273:6613. -->
              <button
                aria-label="Histórico"
                class="rlt-icon"
                type="button"
                @click="openPanel('history')"
              >
                <img
                  alt=""
                  :src="asset('gift.svg')"
                />
              </button>
            </div>

            <img
              alt="Apostatudo"
              class="rlt-logo"
              :src="asset('logo.png')"
            />

            <button
              aria-label="Fechar"
              class="rlt-icon"
              type="button"
              @click="close"
            >
              <img
                alt=""
                :src="asset('close.svg')"
              />
            </button>
          </header>

          <!-- Scrim invisível: fecha o dropdown ao clicar fora dele. -->
          <div
            v-if="menuOpen"
            class="rlt-menu-scrim"
            @click="menuOpen = false"
          />

          <p class="rlt-tagline">
            {{ tagline }}
          </p>

          <div class="rlt-stage">
            <div
              v-if="status === 'loading' || status === 'idle'"
              class="rlt-state"
            >
              Carregando roleta diária...
            </div>

            <!-- 'unavailable' e 'ineligible' não chegam aqui: quem sobe nesses
                 estados é o BaseNoticeSheet, no lugar desta folha. -->
            <div
              v-else-if="status === 'error'"
              class="rlt-state"
            >
              <p>{{
                errorMessage || 'Não foi possível carregar a roletinha.'
              }}</p>
              <button
                class="rlt-retry"
                type="button"
                @click="store.init()"
              >
                Tentar novamente
              </button>
            </div>

            <div
              v-else
              v-show="!panelSettled"
              class="rlt-wheel-wrap"
            >
              <!-- Ribbon arqueado (base + ROLETA + GRATUITA) ATRÁS da roda: a
                   roda cobre a base dele e ele "encaixa" atrás, como no Figma.
                   PNG único @4x rasterizado da composição do Figma (o notch do
                   pino fica vazio; o pino é uma camada separada em z-30). -->
              <div class="rlt-banner">
                <img
                  alt="Roleta Gratuita"
                  class="rlt-banner__img"
                  :src="asset('banner.png')"
                />
              </div>

              <RouletteWheel
                ref="wheel"
                :prizes="prizes"
                :ring-custom="detail?.ringCustom"
                :spin-duration-ms="detail?.spinDurationMs"
                @tick="onPinTick"
              >
                <template #center>
                  <button
                    class="rlt-spin"
                    :class="{ 'rlt-spin--countdown': showCountdown }"
                    :disabled="!canSpin"
                    :style="spinStyle"
                    type="button"
                    @click="handleSpin"
                    @pointerdown="sounds.unlock()"
                    @touchstart.passive="sounds.unlock()"
                  >
                    <!-- Girando: loader dos 3 pontinhos no lugar do texto. -->
                    <span
                      v-if="status === 'spinning'"
                      aria-label="Girando"
                      class="rlt-spin__dots"
                      role="status"
                    >
                      <span class="rlt-spin__dot" />
                      <span class="rlt-spin__dot" />
                      <span class="rlt-spin__dot" />
                    </span>
                    <template v-else-if="showCountdown">
                      <span class="rlt-spin__cd-label">PRÓXIMO EM</span>
                      <span class="rlt-spin__cd-time">{{
                        countdownLabel
                      }}</span>
                    </template>
                    <template v-else>{{ spinCenterLabel }}</template>
                  </button>
                </template>
              </RouletteWheel>

              <!-- Ponteiro (marcador) na fenda do ribbon, na frente de tudo.
                   Camada com a MESMA geometria do ribbon, mas em z-30. -->
              <div class="rlt-marker-layer">
                <img
                  ref="marker"
                  alt=""
                  class="rlt-marker"
                  :src="asset('marcador.png')"
                />
              </div>
            </div>
          </div>

          <!-- Painel do menu (⋮): menu + histórico + termos + prêmios. Só um por
               vez (activePanel); "Voltar" das sub-telas volta pro menu. -->
          <Transition
            name="slide-up"
            @after-enter="panelSettled = true"
            @before-leave="panelSettled = false"
          >
            <div
              v-if="activePanel"
              class="rlt-histpanel"
            >
              <header class="rlt-histpanel__bar">
                <button
                  class="rlt-histpanel__back"
                  type="button"
                  @click="onPanelBack"
                >
                  <svg
                    fill="none"
                    height="16"
                    viewBox="0 0 16 16"
                    width="16"
                  >
                    <path
                      d="M10 13L5 8L10 3"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                    />
                  </svg>
                  Voltar
                </button>
                <h3 class="rlt-histpanel__title">{{ panelTitle }}</h3>
              </header>

              <div class="rlt-histpanel__body">
                <!-- Histórico -->
                <RoulettePrizeHistory v-if="activePanel === 'history'" />

                <!-- Termos e Condições -->
                <div
                  v-else-if="activePanel === 'terms'"
                  class="rlt-terms"
                  v-html="detail?.terms || 'Sem termos disponíveis.'"
                />

                <!-- Prêmios Disponíveis: grid de cards com a imagem de win do
                     prêmio (prize_image_url) + rótulo, do core. -->
                <ul
                  v-else-if="activePanel === 'prizes'"
                  class="rlt-prizes"
                >
                  <li
                    v-for="prize in panelPrizes"
                    :key="prize.id"
                    class="rlt-prize"
                    :style="rarityStyle(prize)"
                  >
                    <!-- Topo: selo da raridade (esq, colorido) + chance (dir). -->
                    <div class="rlt-prize__head">
                      <span
                        v-if="RARITY_LABELS[rarityTier(prize)]"
                        class="rlt-prize__tier"
                      >
                        {{ RARITY_LABELS[rarityTier(prize)] }}
                      </span>
                      <span
                        v-if="probabilityLabel(prize)"
                        class="rlt-prize__chance"
                      >
                        {{ probabilityLabel(prize) }}
                      </span>
                    </div>

                    <div class="rlt-prize__media">
                      <img
                        v-if="prizeDisplayImage(prize)"
                        alt=""
                        class="rlt-prize__img"
                        :src="prizeDisplayImage(prize)"
                      />
                    </div>

                    <!-- Texto centrado: nome do prêmio (prize_name), clampado
                         em 2 linhas com reticências. -->
                    <div class="rlt-prize__body">
                      <span class="rlt-prize__name">{{ prize.prizeName }}</span>
                    </div>

                    <!-- Barrinha da raridade (cor do tier; presente em todos). -->
                    <span class="rlt-prize__accent" />
                  </li>
                </ul>
              </div>
            </div>
          </Transition>

          <!-- Popup de resultado: camada interna do sheet (centrada nele).
               :prize = prêmio sorteado (raridade da fita/glow; null na derrota).
               @close-all: CTA com link fecha a roletinha inteira antes de navegar. -->
          <RoulettePrizePopup
            v-model="popupOpen"
            :popup="activePopup"
            :prize="wonPrize"
            :prize-image-url="activePrizeImage"
            :prize-name="lastSpinResult?.prize?.prizeName ?? null"
            :spins-left="availableSpins"
            @close-all="close"
          />

          <!-- Modal de erro do giro: mesma camada interna do popup de resultado. -->
          <RouletteErrorModal
            v-model="errorOpen"
            :message="errorMessage"
            @close="onErrorClose"
            @retry="onErrorRetry"
          />
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<!-- Transições slide-up/fade replicadas sem scope (mesma estratégia do
     MobilePreview): @keyframes para contornar o reset global de transition. -->
<style>
.slide-up-enter-active {
  animation: rlt-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.slide-up-leave-active {
  animation: rlt-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes rlt-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Dropdown do menu (⋮): fade + leve descida a partir do botão. */
.rlt-dd-enter-active {
  animation: rlt-dd-in 0.16s ease-out forwards;
  transform-origin: top left;
}
.rlt-dd-leave-active {
  animation: rlt-dd-in 0.12s ease-in reverse forwards;
  transform-origin: top left;
}
@keyframes rlt-dd-in {
  0% {
    transform: translateY(-6px) scale(0.97);
    opacity: 0;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
.rlt-overlay {
  // select-none no root: nada dentro do modal (textos, rótulos, termos,
  // histórico) é selecionável; user-drag cobre o arrasto de imagem no WebKit.
  @apply fixed inset-0 z-[9999] flex flex-col justify-end select-none;

  img {
    -webkit-user-drag: none;
  }
}

.rlt-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  animation: rlt-fade-in 0.25s ease forwards;
}

.rlt-backdrop--out {
  animation: rlt-fade-in 0.24s ease reverse forwards;
}

@keyframes rlt-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.rlt-screen {
  @apply relative z-10 flex flex-col items-center w-full overflow-hidden;

  // `hidden` ainda é scroll container: ao abrir uma sub-tela (histórico/termos/
  // prêmios), o ajuste de foco do clique rola esse scrollport escondido e o
  // sheet inteiro fica deslocado (a roda vaza nas bordas inferiores). `clip`
  // não é rolável — browsers antigos ignoram e caem no hidden acima.
  overflow: clip;

  // Escala de camadas internas do modal (consumida também pelo popup de
  // resultado): fonte única da ordem marker/erro(30) < scrim(40) < topbar(50)
  // < painéis(60) < popup(70).
  --rlt-z-panel: 60;
  --rlt-z-popup: 70;

  // Folga no topo: o modal não cobre a tela toda (mostra o backdrop acima).
  // Trava a altura (~frame do Figma) p/ a composição da roda não "esticar" e
  // abrir um vazio entre a tagline e o banner em telas muito altas.
  height: min(84dvh, 600px);
  max-width: 480px;
  margin: 0 auto;
  padding: 18px 16px calc(env(safe-area-inset-bottom, 0px) + 16px);
  border-radius: 22px 22px 0 0;
  background: #000717;
}

// Glow (LIGHT) grande, na cor da borda (do core). Centrado no arco superior da
// roda e grande o bastante pra alcançar atrás da tagline. Fica atrás de tudo.
.rlt-glow {
  @apply absolute pointer-events-none select-none;

  left: 50%;
  top: 60%;
  width: 155%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  filter: blur(28px);
  opacity: 0.55;
  z-index: 0;
}

// Camada das partículas (BaseParticlesField): mesma do glow (z-0), depois dele no
// DOM pra brilhar por cima. O componente não define z-index próprio — quem
// hospeda decide, e aqui z-0 basta porque todo o conteúdo do sheet é posicionado
// com z maior (topbar z-50, roda, botões). O travel padrão do componente (700px)
// já passa da altura máxima do sheet (620px no desktop), então o ciclo reinicia
// fora da vista.
.rlt-particles {
  z-index: 0;
}

.rlt-topbar {
  // z acima do scrim (z-40) p/ o dropdown, ancorado aqui, ficar clicável.
  @apply relative z-50 flex items-center justify-between w-full;
}

// Grupo ⋮ + atalho do histórico (presente), lado a lado como no Figma.
.rlt-topbar__left {
  @apply flex items-center;

  gap: 6px;
}

// Wrapper do botão ⋮ p/ ancorar o dropdown logo abaixo dele.
.rlt-menuwrap {
  @apply relative;
}

// Dropdown do menu (⋮): popover compacto ancorado no botão, não tela cheia.
.rlt-dropdown {
  @apply absolute left-0 flex flex-col;

  top: calc(100% + 8px);
  min-width: 210px;
  padding: 6px;
  border-radius: 14px;
  background: #0b1428;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);

  // Barriguinha (caret) apontando pro botão ⋮: quadradinho girado 45° com só as
  // bordas top/left, alinhado embaixo do botão (canto superior esquerdo).
  &::before {
    content: '';

    @apply absolute;

    top: -6px;
    left: 11px;
    width: 12px;
    height: 12px;
    background: #0b1428;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 3px 0 0 0;
    transform: rotate(45deg);
  }
}

.rlt-dropdown__item {
  @apply w-full text-left text-white text-sm font-semibold;

  padding: 11px 12px;
  border-radius: 9px;

  &:hover,
  &:active {
    background: rgba(255, 255, 255, 0.08);
  }
}

// Scrim transparente que captura o clique-fora p/ fechar o dropdown.
.rlt-menu-scrim {
  @apply absolute inset-0 z-40;
}

.rlt-icon {
  @apply flex items-center justify-center shrink-0;

  width: 30px;
  height: 30px;

  img {
    @apply w-full h-full;
  }
}

.rlt-logo {
  @apply h-[24px] w-auto select-none;
}

.rlt-tagline {
  @apply relative z-10 text-white text-center font-bold mb-1;

  // Tagline mais pra baixo (como no Figma): encosta perto do banner e fecha o
  // vão entre o texto e o topo da roda.
  margin-top: 52px;
  font-size: 20px;
  line-height: 1.3;
  max-width: 332px;
  // Reserva as 2 linhas enquanto a tagline ainda não chegou do core, pra roda
  // não pular quando o texto aparecer.
  min-height: calc(2 * 1.3em);
}

.rlt-stage {
  @apply relative z-10 flex flex-col items-center w-full;

  flex: 1 1 auto;
  min-height: 0;
}

.rlt-state {
  @apply flex flex-col items-center gap-3 text-white/90 text-sm text-center;

  // % de margem se resolve pela LARGURA do container: ~120px num sheet de 480px.
  // É o que centra verticalmente a frase na altura cheia.
  margin-top: 25%;
}

.rlt-retry {
  @apply text-white text-sm underline;
}

// Roda grande posicionada pelo centro bem embaixo: extravasa as laterais e a
// base, só o arco de cima fica visível, como no Figma.
.rlt-wheel-wrap {
  @apply absolute left-1/2;

  top: 86%;
  // Diâmetro na proporção do Figma (~127% da largura). Banner = 72% disso ≈ 92%
  // da tela, então encaixa sem estourar as pontas ROLETA/GRATUITA.
  width: min(127vw, 520px);
  transform: translate(-50%, -50%);

  // z-index acima do ribbon: a roda cobre a base do ribbon (ele encaixa atrás).
  :deep(.wheel) {
    @apply w-full;

    z-index: 2;
  }
}

// Ribbon arqueado ATRÁS da roda (z-1 < roda z-2): base + títulos. Só o arco de
// cima com o texto fica visível; a base tucka atrás da roda, como no Figma.
.rlt-banner {
  @apply absolute left-1/2 z-[1] pointer-events-none;

  // Encostado na borda da roda (letras coladas na roleta); o pino acompanha.
  top: -12%;
  width: 72%;
  transform: translateX(-50%);
  aspect-ratio: 362 / 125;
  filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.45));
}

.rlt-banner__img {
  @apply absolute inset-0 w-full h-full select-none;
}

// Camada do marcador: mesma geometria do ribbon (362/125), mas na frente (z-30),
// para o ponteiro assentar exatamente na fenda do ribbon.
.rlt-marker-layer {
  @apply absolute left-1/2 z-30 pointer-events-none;

  // Mesma geometria do ribbon (acompanha o banner ao aproximar da roda).
  top: -13%;
  width: 72%;
  transform: translateX(-50%);
  aspect-ratio: 362 / 125;
}

// Marcador: no Figma a arte do pino EXTRAVASA a caixa do frame (inset ~-42% x /
// -27% y), então a largura visível é ~30.5% do ribbon — não os 16.59% do frame.
// Centralizado no ribbon; altura AUTO p/ manter o aspecto natural do pino.
// PNG @~4x rasterizado do vetor do Figma (a sombra/gradiente do SVG serrilhava
// no browser e a drop-shadow do CSS somava um halo cinza); a sombra já vem
// embutida no PNG, então não aplicamos filter aqui.
.rlt-marker {
  @apply absolute select-none;

  left: 50%;
  // Um pouco menor e descido em relação ao encaixe exato do Figma.
  top: -4%;
  width: 27%;
  transform: translateX(-50%);
  // Pivô do "clac" na cabeça do pino (topo), pra bascular como um ponteiro.
  transform-origin: 50% 14%;
}

// Botão central rosa "GIRAR" (estilo PLAY do Figma)
.rlt-spin {
  @apply flex items-center justify-center text-center text-white font-extrabold rounded-full;

  width: clamp(72px, 20vw, 92px);
  height: clamp(72px, 20vw, 92px);
  font-size: 14px;
  line-height: 1.05;
  white-space: pre-line;
  letter-spacing: 0.5px;
  background: radial-gradient(circle at 50% 35%, #ff4fae 0%, #d2147f 70%);
  border: 4px solid #ff7ac4;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.45),
    inset 0 2px 6px rgba(255, 255, 255, 0.35);

  // O centro da roda nunca fica translúcido: ele é parte da arte da roleta e
  // qualquer fade deixa o miolo do SVG vazando por trás. Precisa ser explícito
  // porque o main.scss aplica opacity-90 em TODO `button:hover`; o :disabled
  // cobre o giro em andamento e o estado sem giros/countdown.
  opacity: 1;

  &:hover,
  &:disabled,
  &:disabled:hover {
    opacity: 1;
  }
}

// Loader dos 3 pontinhos enquanto a roda gira (substitui o "..."): bolinhas
// quicando em onda. São elementos com border-radius, e NÃO círculos desenhados
// com radial-gradient no background: a parada de cor do gradiente é rasterizada
// sem anti-aliasing e a borda sai em escadinha em DPR 1 (visível a olho nu no
// desktop) — o border-radius o browser rasteriza com AA e sai liso.
.rlt-spin__dots {
  @apply flex items-center justify-center;

  gap: 7px;
  height: 24px;
}

.rlt-spin__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 9px rgba(255, 255, 255, 0.5);
  animation: rlt-spin-dots 0.9s ease-in-out infinite;

  // Defasagem entre as três: a onda corre da esquerda pra direita.
  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }
}

// Só transform + opacity (compositor), em @keyframes por causa do reset global
// de transition do main.scss.
@keyframes rlt-spin-dots {
  0%,
  100% {
    transform: translateY(4px) scale(0.82);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-4px) scale(1);
    opacity: 1;
  }
}

// Estado countdown: empilha "PRÓXIMO EM" + relógio HH:MM:SS dentro do botão.
.rlt-spin--countdown {
  @apply flex-col gap-0.5;

  letter-spacing: 0;
}

.rlt-spin__cd-label {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.3px;
  opacity: 0.85;
}

.rlt-spin__cd-time {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
}

// Painel de histórico sobreposto (acima do topbar/scrim quando aberto)
.rlt-histpanel {
  @apply absolute inset-0 flex flex-col;

  z-index: var(--rlt-z-panel);

  padding: 18px 16px calc(env(safe-area-inset-bottom, 0px) + 16px);
  // Raio só no topo (visível durante o slide-up). Embaixo o painel fica
  // QUADRADO de propósito: ele pinta até a quina e o clip arredondado do
  // .rlt-screen faz o arredondamento — se o painel tiver raio próprio, a
  // cobertura termina antes da curva do clip e o anti-aliasing deixa vazar
  // uma frisa da roda (clara) nas quinas inferiores.
  border-radius: 22px 22px 0 0;
  background: #000717;
}

.rlt-histpanel__bar {
  @apply relative flex items-center justify-center w-full mb-4 shrink-0;

  height: 30px;
}

.rlt-histpanel__back {
  @apply absolute left-0 flex items-center gap-1 text-white/90 text-sm font-semibold;
}

.rlt-histpanel__title {
  @apply text-white text-base font-extrabold;
}

.rlt-histpanel__body {
  @apply flex-1 overflow-y-auto;

  overscroll-behavior: contain;
}

// Termos e condições
.rlt-terms {
  @apply text-white/85 text-sm leading-relaxed;

  white-space: pre-line;
}

// Prêmios disponíveis: grid de cards com a imagem de win + rótulo (do core).
.rlt-prizes {
  @apply grid pb-2;

  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
}

// Card de prêmio (layout do Figma): gradiente navy, borda azulada, selo +
// chance no topo, imagem, texto centrado e barrinha da raridade na base. A
// raridade pinta só o selo/chance/barra via --rar-color / --rar-glow.
.rlt-prize {
  @apply relative flex flex-col items-center justify-between overflow-hidden;

  // Cor/glow da raridade vêm por :style (rarityStyle); defaults = comum.
  --rar-color: #ffffff;
  --rar-glow: #ffffff;

  height: 140px;
  padding: 9px 7px 0;
  border: 1px solid #203570;
  border-radius: 10px;
  background: linear-gradient(180deg, #08112d 0%, #111827 100%);
}

// Topo: selo (esq) + chance (dir).
.rlt-prize__head {
  @apply flex items-start justify-between w-full font-bold;

  font-size: 8px;
  line-height: 1.15;
}

.rlt-prize__tier {
  color: var(--rar-color);
  text-shadow: 0 1px 12px var(--rar-glow);
}

.rlt-prize__chance {
  @apply text-right;

  color: rgba(235, 235, 236, 0.5);
}

.rlt-prize__media {
  @apply flex items-center justify-center w-full;

  // Altura travada: a imagem não estica o card (contain dentro da área).
  height: 68px;
}

.rlt-prize__img {
  @apply max-w-full max-h-full object-contain;

  border-radius: 6px;
}

// Texto centrado: nome do prêmio, clampado em 2 linhas com reticências.
.rlt-prize__body {
  @apply flex flex-col items-center text-center w-full text-white font-bold leading-tight;

  font-size: 10px;
}

.rlt-prize__name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

// Barrinha da raridade: pill de topo arredondado, colado na base, com glow.
.rlt-prize__accent {
  width: 54px;
  height: 5px;
  border-radius: 11px 11px 0 0;
  background: var(--rar-color);
  box-shadow: 0 1px 28px var(--rar-glow);
}

// Desktop: modal normal (card centrado), em vez do bottom-sheet do mobile.
@media (min-width: 1024px) {
  .rlt-overlay {
    justify-content: center;
  }

  // Mantém o aspecto do design (~0.67 L/A): p/ 420px de largura, ~620px de
  // altura — senão o card fica alto demais e reabre o vão acima do banner.
  .rlt-screen {
    height: min(84vh, 620px);
    max-width: 420px;
    border-radius: 22px;
  }

  // Sem raio inferior próprio também no desktop (ver comentário no
  // .rlt-histpanel): o clip do .rlt-screen arredonda as quinas de baixo.
  .rlt-histpanel {
    border-radius: 22px 22px 0 0;
  }
}
</style>
